/* eslint-disable no-unused-vars */
import { defineStore } from "pinia";
import http from "../config/apiConfig";
import { useUserSecurityStore } from "./usersecurity";

export const useShopGroupStore = defineStore("shopgroup", {
  state: () => ({
    // cache รายการกลุ่มสาขา (shared ข้ามทุก component instance)
    cachedGroups: [],
    // Promise กำลังโหลดอยู่ — ป้องกันยิง API ซ้ำเมื่อหลาย instance mount พร้อมกัน
    _loadingPromise: null,
  }),
  actions: {
    /** โหลดรายการกลุ่มสาขาพร้อม info — ถ้ามี cache แล้วคืนทันที */
    async loadGroupsWithCache(userUid = "") {
      if (this.cachedGroups.length > 0) return this.cachedGroups;
      if (this._loadingPromise) return this._loadingPromise;
      this._loadingPromise = this._fetchGroups(userUid);
      try {
        this.cachedGroups = await this._loadingPromise;
      } finally {
        this._loadingPromise = null;
      }
      return this.cachedGroups;
    },

    /** 🟢 1. แก้ไขตรงนี้: ล้าง Cache ทิ้งให้เกลี้ยงจริงๆ ไม่ต้องแอบดึงซ้อนในนี้ */
    clearGroupsCache() {
      this.cachedGroups = [];
      this._loadingPromise = null;
    },

    /** 🟢 2. เพิ่มฟังก์ชันนี้: สำหรับบังคับดึงข้อมูลใหม่จาก API ทันทีแบบไม่สนเงื่อนไขความยาวคลัง */
    async refetchGroups(userUid = "") {
      this._loadingPromise = this._fetchGroups(userUid); 
      try {
        this.cachedGroups = await this._loadingPromise;
      } finally {
        this._loadingPromise = null;
      }
      return this.cachedGroups;
    },

    /** ดึงข้อมูลจาก API (ใช้ภายใน) */
    async _fetchGroups(userUid = "") {
      const filter = {};
      if (userUid) filter.userUid = userUid;
      const body = { page: { index: 0, size: 100, sort: "" }, filter };
      let res = await http.post("/master-svc/shop-grp/list", body);
      let rawList = res?.data?.data?.list || res?.data?.data || res?.data?.list || res?.data;
      if (!Array.isArray(rawList) || rawList.length === 0) {
        // Fallback: ลองดึงโดยไม่กรอง userUid
        res = await http.post("/master-svc/shop-grp/list", { page: { index: 0, size: 100, sort: "" }, filter: {} });
        rawList = res?.data?.data?.list || res?.data?.data || res?.data?.list || res?.data;
      }
      if (!Array.isArray(rawList)) return [];
      // ดึง info ทุกกลุ่มพร้อมกัน เพื่อให้ได้ members ครบ
      const infoList = await Promise.all(
        rawList.map(async (g) => {
          try {
            const infoRes = await http.get("/master-svc/shop-grp/info/" + g.uid);
            const infoData = infoRes?.data?.data || infoRes?.data || null;
            if (infoData) return infoData;
          } catch (_e) { /* ใช้ข้อมูลจาก list แทน */ }
          return g;
        })
      );
      return infoList.map((g) => ({
        uid: String(g.uid || ""),
        name: g.name || "",
        version: String(g.version ?? "1"),
        members: Array.isArray(g.members) ? g.members : [],
      }));
    },
    async introspectFetch() {
      const userSStore = useUserSecurityStore();
      const key = JSON.parse(localStorage.getItem("FR-SDK-nvtx-admin") || "null");
      const token = key?.accessToken || "";
      return userSStore.introspect(token);
    },
    async shopGroupListFetch(payload = {}) {
      const filter = {};
      if (payload?.userUid) {
        filter.userUid = payload.userUid;
      }
      const body = {
        page: {
          index: payload?.index ?? 0,
          size: payload?.size ?? 100,
          sort: payload?.sort ?? "",
        },
        filter: filter,
      };
      return new Promise((resolve, reject) => {
        try {
          const pathUrl = "/master-svc/shop-grp/list";
          const response = http.post(pathUrl, body);
          resolve(response);
        } catch (e) {
          reject(e);
        }
      });
    },
    async shopGroupInfoFetch(payload) {
      return new Promise((resolve, reject) => {
        try {
          const uid = typeof payload === "object" && payload !== null ? payload.uid : payload;
          const pathUrl = "/master-svc/shop-grp/info/" + uid;
          const response = http.get(pathUrl);
          resolve(response);
        } catch (e) {
          reject(e);
        }
      });
    },
    async shopGroupAddFetch(payload) {
      const body = {
        name: payload.name,
        members: payload.members,
      };
      return new Promise((resolve, reject) => {
        try {
          const pathUrl = "/master-svc/shop-grp/new";
          const response = http.post(pathUrl, body);
          resolve(response);
        } catch (e) {
          reject(e);
        }
      });
    },
    async shopGroupUpdateFetch(payload) {
      const body = {
        uid: payload.uid,
        name: payload.name,
        members: payload.members,
        version: payload.version,
      };
      return new Promise((resolve, reject) => {
        try {
          const pathUrl = "/master-svc/shop-grp/update";
          const response = http.put(pathUrl, body);
          resolve(response);
        } catch (e) {
          reject(e);
        }
      });
    },
    async shopGroupDeleteFetch(payload) {
      return new Promise((resolve, reject) => {
        try {
          const uid = typeof payload === "object" && payload !== null ? payload.uid : payload;
          const pathUrl = "/master-svc/shop-grp/delete/" + uid;
          const response = http.delete(pathUrl);
          resolve(response);
        } catch (e) {
          reject(e);
        }
      });
    },
  },
});
